import { type Map } from "maplibre-gl";
import { useSyncExternalStore } from "react";
import { referenceEqual } from "../../shared";

let mapInstance: Map | undefined;

const listeners = new Set<() => void>();

/**
 * Notifies subscribers that the map instance reference may have changed.
 */
const notify = (): void => {
    for ( const listener of listeners ) {
        listener();
    }
};

/**
 * Returns the current MapLibre instance, if registered.
 *
 * @returns Map instance or `undefined` when not yet mounted.
 */
export const getMapInstance = (): Map | undefined => {
    return mapInstance;
};

/**
 * Registers or clears the MapLibre instance (impérative, not in reactive slice state).
 *
 * @param map Map instance or `undefined` when unmounting.
 */
export const setMapInstance = ( map: Map | undefined ): void => {
    if ( referenceEqual( mapInstance, map ) ) {
        return;
    }

    mapInstance = map;
    notify();
};

/**
 * Subscribes to map instance registration changes for `useSyncExternalStore`.
 *
 * @param listener Change listener.
 * @returns Unsubscribe function.
 */
export const subscribeMapInstance = ( listener: () => void ): () => void => {
    listeners.add( listener );

    return () => {
        listeners.delete( listener );
    };
};

/**
 * React hook exposing the current MapLibre instance (re-renders on register/unregister).
 *
 * @returns Map instance or `undefined`.
 */
export const useMapInstance = (): Map | undefined => {
    return useSyncExternalStore( subscribeMapInstance, getMapInstance, getMapInstance );
};

/**
 * Resets registry state.
 *
 * @remarks Intended for unit tests only.
 */
export const resetMapInstanceRegistryForTests = (): void => {
    mapInstance = undefined;
    listeners.clear();
};
